import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import { PageHero } from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import { useSeo } from "@/hooks/useSeo";
import { guideSteps, guideToc, myths, prepChecklist, safetyTips, typeAdvice } from "@/mocks/guide";

const guideSchema = [
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "如何向暗线提交一条独家爆料线索",
    description:
      "从整理事实边界、保留原始载体到提交并约定沟通方式，帮助知情者安全地完成一次线索投递。",
    inLanguage: "zh-CN",
    totalTime: "PT15M",
    step: guideSteps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.title,
      text: step.desc,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://hongshanyang.cn/" },
      { "@type": "ListItem", position: 2, name: "投稿指南", item: "https://hongshanyang.cn/guide" },
    ],
  },
];

export default function GuidePage() {
  const [activeId, setActiveId] = useState(guideToc[0].id);

  useSeo({
    title: "投稿指南｜如何安全地提交独家爆料线索",
    description:
      "独家爆料投稿前的完整指南：如何整理材料、保留原始载体、保护自身安全，以及文件类、数据类、人证类线索的整理建议与五个常见误区。",
    keywords: "独家爆料投稿指南,线索整理,匿名投稿安全,知情者投稿,爆料材料准备",
    path: "/guide",
    schema: guideSchema,
  });

  useEffect(() => {
    const sections = guideToc
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0 || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <PageHero
          eyebrow="GUIDE · 投稿指南"
          title="先保护好自己，再交出材料"
          description="这份指南写给第一次提供线索的人。从整理事实边界、保留原始载体，到判断哪些内容不该交出去，我们说清楚每一步。"
          crumbs={[{ label: "首页", to: "/" }, { label: "投稿指南" }]}
          image="https://readdy.ai/api/search-image?query=dark%20desk%20surface%20with%20neatly%20stacked%20folders%2C%20a%20magnifying%20glass%20and%20index%20cards%20under%20a%20warm%20focused%20lamp%2C%20deep%20charcoal%20shadows%2C%20amber%20highlight%2C%20fine%20film%20grain%2C%20top%20down%20investigative%20editorial%20photography%2C%20no%20text&width=1920&height=900&seq=anxian-guide-hero&orientation=landscape"
          imageAlt="台灯下整齐堆叠的材料与放大镜，代表线索整理与核实"
          meta={
            <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-background-200/85">
              <span className="flex items-center gap-2">
                <i className="ri-timer-line text-accent-300"></i>
                阅读约 8 分钟
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-list-check-2 text-accent-300"></i>
                含 5 个可跳转章节
              </span>
            </div>
          }
        />

        <section className="w-full bg-background-50 py-12 md:py-20">
          <div className="mx-auto w-full max-w-[1320px] px-5 md:px-8">
            <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto pb-2 lg:hidden">
              {guideToc.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-xs transition-colors ${
                    activeId === item.id
                      ? "border-primary-300 bg-primary-100 text-primary-800"
                      : "border-background-300 bg-background-50 text-foreground-600"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-1 gap-12 lg:mt-0 lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-16">
              <aside className="hidden lg:block">
                <div className="sticky top-28">
                  <p className="font-heading text-xs font-semibold tracking-[0.2em] text-foreground-500">
                    本页目录
                  </p>
                  <nav className="mt-5 space-y-1 border-l border-background-200" aria-label="指南目录">
                    {guideToc.map((item) => (
                      <a
                        key={item.id}
                        href={`#${item.id}`}
                        className={`block cursor-pointer border-l-2 py-2 pl-4 text-sm transition-colors ${
                          activeId === item.id
                            ? "border-primary-500 font-medium text-primary-700"
                            : "border-transparent text-foreground-600 hover:text-foreground-950"
                        }`}
                      >
                        {item.label}
                      </a>
                    ))}
                  </nav>

                  <Link
                    to="/submit"
                    className="mt-8 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-shield-keyhole-line text-base"></i>
                    前往提交
                  </Link>
                </div>
              </aside>

              <div className="space-y-16 md:space-y-20">
                <section id="prepare">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    提交前先准备什么
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
                    不需要写成正式报告，也不必等到材料齐全。但有四件事如果提前想清楚，后面的核实会顺畅得多。
                  </p>
                  <div className="mt-8 space-y-4">
                    {prepChecklist.map((item, index) => (
                      <Reveal key={item.title} delay={index * 50}>
                        <div className="flex gap-5 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 font-heading text-sm font-bold text-accent-800">
                            {index + 1}
                          </span>
                          <div>
                            <h3 className="font-heading text-base font-semibold text-foreground-950">
                              {item.title}
                            </h3>
                            <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </section>

                <section id="steps">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    三步完成投递
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
                    每一步都可以停下来重新判断。我们不会因为你中途改变主意而追问理由。
                  </p>
                  <div className="mt-8 space-y-6">
                    {guideSteps.map((step) => (
                      <article
                        key={step.step}
                        className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
                      >
                        <div className="flex items-baseline gap-4">
                          <span className="font-heading text-2xl font-bold text-primary-400">{step.step}</span>
                          <h3 className="font-heading text-lg font-semibold text-foreground-950">{step.title}</h3>
                        </div>
                        <p className="mt-4 text-sm leading-7 text-foreground-600">{step.desc}</p>
                        <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-50 p-3.5 text-xs leading-6 text-secondary-900">
                          <i className="ri-lightbulb-line mt-0.5 text-sm text-secondary-600"></i>
                          {step.tip}
                        </p>
                      </article>
                    ))}
                  </div>
                </section>

                <section id="safety">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    如何保护你自己
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
                    身份暴露往往不是来自技术溯源，而是来自日常习惯。以下六条，是投稿者最常忽略的部分。
                  </p>
                  <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {safetyTips.map((tip) => (
                      <div
                        key={tip.title}
                        className="h-full rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                          <i className={`${tip.icon} text-lg`}></i>
                        </span>
                        <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                          {tip.title}
                        </h3>
                        <p className="mt-2 text-sm leading-7 text-foreground-600">{tip.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="by-type">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    不同线索的整理建议
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
                    不同类型的材料，核验方式不一样。按下面的思路整理，能显著提高线索被采纳的概率。
                  </p>
                  <div className="mt-8 space-y-4">
                    {typeAdvice.map((item) => (
                      <div
                        key={item.title}
                        className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-6 sm:flex-row sm:gap-6"
                      >
                        <div className="flex shrink-0 items-center gap-3 sm:w-44 sm:flex-col sm:items-start">
                          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                            <i className={`${item.icon} text-xl`}></i>
                          </span>
                          <span className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">
                            {item.label}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                          <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="myths">
                  <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">五个常见误区</h2>
                  <p className="mt-4 max-w-2xl text-sm leading-7 text-foreground-600 md:text-[15px]">
                    这些说法流传得很广，但多数情况下并不成立。提前知道，可以少走弯路。
                  </p>
                  <div className="mt-8 space-y-5">
                    {myths.map((item, index) => (
                      <Reveal key={item.claim} delay={index * 50}>
                        <div className="overflow-hidden rounded-lg border border-background-200">
                          <div className="flex items-center gap-3 bg-background-100 px-5 py-4 md:px-6">
                            <i className="ri-close-circle-line text-lg text-primary-500"></i>
                            <p className="font-heading text-sm font-semibold text-foreground-800 md:text-base">
                              {item.claim}
                            </p>
                          </div>
                          <div className="flex items-start gap-3 bg-background-50 px-5 py-5 md:px-6">
                            <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600"></i>
                            <p className="text-sm leading-7 text-foreground-600">{item.truth}</p>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </section>

                <div className="rounded-xl border border-primary-200 bg-primary-50 p-7 md:p-9">
                  <h2 className="font-heading text-xl font-bold text-primary-900 md:text-2xl">
                    看完了，可以开始投递
                  </h2>
                  <p className="mt-3 max-w-xl text-sm leading-7 text-primary-900/85">
                    如果还有不放心的地方，可以只留下联系方式先聊聊。不确定的地方，我们会替你一起判断。
                  </p>
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <Link
                      to="/submit"
                      className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-shield-keyhole-line text-base"></i>
                      前往提交通道
                    </Link>
                    <Link
                      to="/faq"
                      className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-7 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                    >
                      还有疑问，看常见问题
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}